import type { W_EmployeeUtilization_FindCompanyTable_VM_Labor } from 'ab-api/web/employeeUtilization/findCompanyTable/viewModel';
import type { W_EmployeeUtilization_FindCompanyTable_VM } from 'ab-api/web/employeeUtilization/findCompanyTable/viewModel';

import { somePredicates } from '@acceligentllc/shared/utils/array';
import { moneyNormalizer } from '@acceligentllc/shared/utils/formatting';

import type { CDLStatus } from '@acceligentllc/shared/enums/cdlStatus';

import { buildTable } from 'af-utils/table.utils';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import type { TableQuery, TableSortBy } from 'ab-common/dataStructures/tableQuery';

const CSV_HEADER_KEYS = [
	'Name',
	'Scheduled',
	'Not Scheduled',
	'Not Available',
	'Daily Revenue',
	'Total Revenue',
	'skill.0.name',
	'skill.0.color',
];

/** Used for empty skills in CSV */
const EMPTY_SKILL = {
	name: '',
	color: '',
};

const DEFAULT_SORT_BY: TableSortBy[] = [{ id: 'fullName', desc: false }, { id: 'timePeriodId', desc: true }];

function _getUtilizationRowsFilterByTextPredicate(text: string): Nullable<ArrayPredicate<W_EmployeeUtilization_FindCompanyTable_VM_Labor>> {
	const trimmedText = (text ?? '').trim();
	if (!trimmedText) {
		return null;
	}
	const uppercaseText = trimmedText.toUpperCase();

	const filterFunctions: ((_e: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => boolean)[] = [
		(_e: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => _e.timePeriodId.toString().toUpperCase().includes(uppercaseText),
		(_e: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => {
			return _e.lastName.toUpperCase().startsWith(uppercaseText) || _e.firstName.toUpperCase().startsWith(uppercaseText);
		},
	];

	if (trimmedText.length <= 2) {
		return somePredicates(filterFunctions);
	}
	// if length is more then 2 search the middle of the string, not just the start:
	filterFunctions[1] = (_e: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => _e.fullName.toUpperCase().includes(uppercaseText);

	return somePredicates(filterFunctions);
}

function _filterUtilizationRowsByText(
	rows: W_EmployeeUtilization_FindCompanyTable_VM_Labor[],
	text: string
): W_EmployeeUtilization_FindCompanyTable_VM_Labor[] {
	const predicate = _getUtilizationRowsFilterByTextPredicate(text);
	if (!predicate?.length) {
		return rows;
	}
	return rows.filter(predicate);
}

export const toCSVData = (viewModels: W_EmployeeUtilization_FindCompanyTable_VM_Labor[]): string[][] => {
	let maxNumberOfSkills = 1;	// if skills are able to be shown on export, the first group of columns should always be visible
	const header: string[] = [...CSV_HEADER_KEYS];

	// We should expand header keys (if there are users that have more than one skill)
	viewModels.forEach((_entry: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => {
		if (_entry.skills.length > maxNumberOfSkills) {
			for (let i = maxNumberOfSkills; i < _entry.skills.length; i++) {
				header.push(`skill.${i}.name`, `skill.${i}.color`);
			}
			maxNumberOfSkills = _entry.skills.length;
		}
	});

	const rows: string[][] = viewModels.map((_entry: W_EmployeeUtilization_FindCompanyTable_VM_Labor) => {
		const _row: string[] = [
			_entry.fullName,
			`${_entry.daysAssigned} days`,
			`${_entry.daysAvailable} days`,
			`${_entry.daysUnavailable} days`,
			`${moneyNormalizer(_entry.dailyRevenue)}`,
			`${moneyNormalizer(_entry.totalRevenue)}`,
		];
		for (let _index = 0; _index < maxNumberOfSkills; _index++) {
			const _skill = _entry.skills[_index] || EMPTY_SKILL;
			_row.push(_skill.name, _skill.color);
		}
		return _row;
	});

	return [header, ...rows];
};

export const getTable = (
	tableRequest: TableQuery,
	selectedSkillIds: Nullable<number[]>,
	cdlOption: Nullable<CDLStatus>,
	data: W_EmployeeUtilization_FindCompanyTable_VM
): Nullable<TableContent<W_EmployeeUtilization_FindCompanyTable_VM_Labor>> => {
	let rows: W_EmployeeUtilization_FindCompanyTable_VM_Labor[] = [];
	const sortBy: TableSortBy[] = !tableRequest.sortBy?.length ? DEFAULT_SORT_BY : tableRequest.sortBy;

	if (!selectedSkillIds?.length) {
		rows = data.rows;
	} else {	// filter by skills
		rows = data.rows.filter((_row) => {
			const { id: employeeId } = _row;

			return selectedSkillIds.every((_skillId) => {
				return data.employeeIdsBySkillId[_skillId]?.includes(employeeId.toString());
			});
		});
	}

	if (cdlOption !== null) {
		rows = rows.filter((_row) => _row.cdlStatus === cdlOption);
	}

	return buildTable({ ...tableRequest, sortBy }, rows, _filterUtilizationRowsByText);
};
